import { Input, Spinner } from '@ohear/ui'
import { type FormEvent, useEffect, useState } from 'react'
import { Panel, SlpButton } from '../components/slp'
import { navigate } from '../lib/router'
import { getSupabase } from '../lib/supabase'

/**
 * /reset-password — landing for Supabase password-recovery emails (F4).
 *
 * The recovery link redirects here with a PKCE ?code=; the supabase client
 * (detectSessionInUrl: true) exchanges it on init and fires PASSWORD_RECOVERY.
 * We accept either that event or any live session (updateUser works for both
 * a recovery session and a normally signed-in user changing their password).
 * If neither shows up within the grace window, the link is missing/expired.
 */

type Phase = 'checking' | 'ready' | 'no-link' | 'done'

const GRACE_MS = 2500

export function ResetPasswordPage() {
  const [phase, setPhase] = useState<Phase>('checking')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const supabase = getSupabase()
    let settled = false
    const ready = () => {
      settled = true
      setPhase((p) => (p === 'checking' || p === 'no-link' ? 'ready' : p))
    }
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || session) ready()
    })
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) ready()
    })
    const timer = window.setTimeout(() => {
      if (!settled) setPhase((p) => (p === 'checking' ? 'no-link' : p))
    }, GRACE_MS)
    return () => {
      sub.subscription.unsubscribe()
      window.clearTimeout(timer)
    }
  }, [])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Passwords need at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    const { error: err } = await getSupabase().auth.updateUser({ password })
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    setPhase('done')
  }

  // Desktop-locked SLP shell (1180px) — matches AuthScreen / the /join error
  // card (W1 S1). DesktopGate intercepts <900px, so this only renders on desktop.
  return (
    <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)]">
      <div className="mx-auto flex min-h-screen w-[1180px] flex-col justify-center">
        <div className="mx-auto w-[472px]">
          <h1 className="mb-1 text-[28px] font-semibold tracking-tight text-[var(--slp-text)]">
            OHEAR Session
          </h1>
          <p className="mb-6 text-[14px] text-[var(--slp-text-dim)]">Set a new password.</p>

          <Panel title="Reset password">
            {phase === 'checking' ? (
              <div className="flex items-center gap-3 py-2">
                <Spinner />
                <span className="text-[13px] text-[var(--slp-text-dim)]">
                  Checking your reset link…
                </span>
              </div>
            ) : null}

            {phase === 'no-link' ? (
              <div className="flex flex-col gap-3" data-testid="rt-reset-no-link">
                <p className="text-[13px] text-[var(--slp-text)]">
                  This page sets a new password after you arrive from the reset email. No reset link
                  detected — request one from the sign-in screen.
                </p>
                <SlpButton variant="secondary" onClick={() => navigate('/new')}>
                  Back to sign in
                </SlpButton>
              </div>
            ) : null}

            {phase === 'ready' ? (
              <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-3">
                <Input
                  type="password"
                  placeholder="New password (min 8 characters)"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="rt-reset-password"
                />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="rt-reset-confirm"
                />
                {error ? <p className="text-[13px] text-[var(--amber)]">{error}</p> : null}
                <SlpButton type="submit" disabled={busy} data-testid="rt-auth-reset-submit">
                  {busy ? 'Updating…' : 'Set new password'}
                </SlpButton>
              </form>
            ) : null}

            {phase === 'done' ? (
              <div className="flex flex-col gap-3" data-testid="rt-reset-done">
                <p className="text-[13px] text-[var(--slp-text)]">
                  Password updated — you are signed in with the new password.
                </p>
                <SlpButton onClick={() => navigate('/new')}>Go to sessions</SlpButton>
              </div>
            ) : null}
          </Panel>
        </div>
      </div>
    </div>
  )
}
